import { Trans, useLingui } from '@lingui/react/macro';
import {
  ArrowLeft,
  Building2,
  ChevronLeft,
  Download,
  FileText,
  MapPin,
  Pencil,
  Save,
  ToggleLeft,
  Zap,
} from 'lucide-react';

import { StatusBadge } from '@/components/templates/status-badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { LifecycleStatus } from '@/hooks/use-wizard-state';
import type { AdvancedSettings, RotaTemplateScope } from '@/types/template';

import { RulesPopover } from './rules-popover';

/**
 * Build workspace header card: a back button, the template name + draft/active
 * status, a meta row (scope chip, description, "Edit details", scheduling
 * rules), and two independent actions: Save (writes the definition, never
 * changes the lifecycle) and Publish/Unpublish (changes only the lifecycle).
 * Publish is gated on there being at least one pattern; both lifecycle
 * directions are blocked, with a hint, while there are unsaved changes.
 * Replaces the wizard's Step 4 review.
 */
export function BuildHeader({
  name,
  scope,
  siteCount,
  description,
  status,
  advancedSettings,
  onAdvancedChange,
  onEditDetails,
  onCancel,
  onSave,
  onToggleLifecycle,
  onExportJson,
  canPublish,
  lifecycleBlockedReason,
  saving = false,
}: BuildHeaderProps) {
  const { t } = useLingui();
  const isOrganisationWide = (scope.sites?.length ?? 0) === 0;
  const scopeLabel = isOrganisationWide
    ? t`All sites`
    : siteCount === 1
      ? t`1 site`
      : t`${siteCount} sites`;

  const isDraft = status === 'draft';
  const lifecycleButton = (
    <Button
      className="rotaready:!h-[30px] rotaready:py-0"
      onClick={onToggleLifecycle}
      disabled={saving || lifecycleBlockedReason !== null || (isDraft && !canPublish)}
    >
      {isDraft ? <Zap className="size-4" /> : <ToggleLeft className="size-4" />}
      {isDraft ? <Trans>Publish</Trans> : <Trans>Unpublish</Trans>}
    </Button>
  );

  return (
    <header className="flex items-center gap-3 rounded-lg border bg-card p-3 shadow-sm rotaready:min-h-12 rotaready:w-full rotaready:flex-wrap rotaready:gap-2 rotaready:rounded-none rotaready:border-0 rotaready:border-b rotaready:border-b-[#bbc3ca] rotaready:px-5 rotaready:py-2 rotaready:shadow-none">
      <Button
        variant="ghost"
        size="icon"
        className="rotaready:size-10 rotaready:text-[var(--grey)] rotaready:hover:text-[var(--grey-160)]"
        aria-label={t`Back`}
        onClick={onCancel}
        disabled={saving}
      >
        <ArrowLeft className="size-4 rotaready:hidden" />
        <ChevronLeft
          data-testid="rotaready-back-icon"
          className="hidden size-4 rotaready:block rotaready:size-5"
        />
      </Button>
      <div className="h-8 w-px shrink-0 bg-border rotaready:hidden" />

      <div className="flex min-w-0 flex-1 flex-col gap-1 rotaready:contents">
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-[length:var(--body-font-size)] leading-[var(--body-line-height)] font-semibold">
            {name}
          </h1>
          <StatusBadge
            status={status}
            icon={isDraft ? <FileText className="size-3" /> : <Zap className="size-3" />}
            label={isDraft ? <Trans>Draft</Trans> : <Trans>Active</Trans>}
            size="xs"
          />
        </div>
        <div className="flex min-w-0 items-center gap-2 text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)] rotaready:contents">
          <span className="flex shrink-0 items-center gap-1 rotaready:hidden">
            {isOrganisationWide ? <Building2 className="size-3" /> : <MapPin className="size-3" />}
            {scopeLabel}
          </span>
          <span className="size-1 shrink-0 rounded-full bg-muted-foreground/40 rotaready:hidden" />
          <span className="truncate rotaready:hidden">
            {description?.trim() || t`No description`}
          </span>
          <Button variant="ghost" size="sm" onClick={onEditDetails} disabled={saving}>
            <Pencil className="size-3" />
            <Trans>Edit details</Trans>
          </Button>
          <div className="h-4 w-px shrink-0 bg-border rotaready:hidden" />
          <div className="rotaready:ml-auto">
            <RulesPopover
              advancedSettings={advancedSettings}
              onChange={onAdvancedChange}
              triggerClassName="rotaready:text-[var(--foreground)] rotaready:hover:text-[var(--foreground)]"
            />
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {onExportJson && (
          <Button
            variant="ghost"
            size="icon"
            className="rotaready:hidden"
            aria-label={t`Export JSON`}
            onClick={onExportJson}
            disabled={saving}
          >
            <Download className="size-4" />
          </Button>
        )}
        <Button
          variant="outline"
          className="rotaready:!h-[30px] rotaready:py-0"
          onClick={onSave}
          disabled={saving}
        >
          <Save className="size-4" />
          <Trans>Save</Trans>
        </Button>
        {lifecycleBlockedReason === 'unsaved' ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>{lifecycleButton}</span>
              </TooltipTrigger>
              <TooltipContent>
                <Trans>Save your changes first</Trans>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          lifecycleButton
        )}
      </div>
    </header>
  );
}

interface BuildHeaderProps {
  /** Template name (or a placeholder for an unnamed new template). */
  name: string;
  /** Template scope, driving the scope chip (empty sites ⇒ organisation-wide). */
  scope: RotaTemplateScope;
  /** Number of selected sites, shown when scope is `specific`. */
  siteCount: number;
  /** Optional description, shown in the meta row. */
  description?: string;
  /** The saved template's lifecycle status (drives the badge and the lifecycle button). */
  status: LifecycleStatus;
  advancedSettings: AdvancedSettings;
  onAdvancedChange: (settings: AdvancedSettings) => void;
  /** Opens the basics modal in edit mode. */
  onEditDetails: () => void;
  /** Returns to the templates list (the back button). */
  onCancel: () => void;
  /** Saves the definition without changing the lifecycle. */
  onSave: () => void;
  /** Publishes a draft or unpublishes a published template, without saving. */
  onToggleLifecycle: () => void;
  /** Exports the current template definition as a JSON file. Omit to hide the control. */
  onExportJson?: () => void;
  /** Whether Publish is allowed (false until at least one pattern exists); doesn't affect Unpublish. */
  canPublish: boolean;
  /** Non-null disables Publish/Unpublish with a hint, because the lifecycle applies to the saved template only. */
  lifecycleBlockedReason: 'unsaved' | null;
  /** Disables the actions while a save is in flight. */
  saving?: boolean;
}
